import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import styles from './HomePage.module.css';

const MATERIALS = [
  {
    name: 'Natural stone',
    title: 'Character, in every surface.',
    body: 'Texture catches the light. Subtle variation gives a room its depth. Explore the materials that make a house feel like home.',
    position: '22% center',
  },
  {
    name: 'Warm timber',
    title: 'A little warmth goes a long way.',
    body: 'The grain of timber brings a natural rhythm to a space. Consider its tone alongside your floors, cabinetry, and everyday life.',
    position: '70% center',
  },
  {
    name: 'Natural light',
    title: 'Room for the light to move.',
    body: 'A window frames more than a view. Think about morning light, evening shade, and the way each room feels throughout the day.',
    position: '95% center',
  },
];
export function HomePage() {
  useDocumentTitle();
  const [material, setMaterial] = useState(0);
  const selected = MATERIALS[material];
  const trackRef = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  /** Select a material (wrapping at the ends) and scroll the carousel to it. */
  const goToMaterial = (index: number) => {
    const next = (index + MATERIALS.length) % MATERIALS.length;
    setMaterial(next);
    const track = trackRef.current;
    if (!track) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: next * track.clientWidth,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  /** After a swipe/scroll settles on a slide, select that material. */
  const handleTrackScroll = () => {
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      const track = trackRef.current;
      if (!track || !track.clientWidth) return;
      setMaterial(Math.round(track.scrollLeft / track.clientWidth));
    }, 90);
  };
  return (
    <>
      <section className={styles.hero} aria-labelledby="hero-title">
        <div className={styles.heroCopy}>
          <p className="eyebrow">Custom homes —</p>
          <h1 id="hero-title">
            Built around
            <br />
            the way
            <br />
            <em>you live.</em>
          </h1>
          <p className={styles.intro}>
            Thoughtful spaces. Considered details.
            <br />A home that feels like yours.
          </p>
          <Link className={styles.editorialLink} to="/contact">
            <span aria-hidden="true">⟶</span> Start a conversation
          </Link>
          <div className={styles.footnote}>
            <span aria-hidden="true">＋</span>
            <p>
              People. Place. Possibility.
              <br />
              <small>A considered approach to home.</small>
            </p>
          </div>
        </div>
        <figure className={styles.heroImage}>
          <img
            src="/images/architecture-concept.webp"
            srcSet="/images/architecture-concept-small.webp 800w, /images/architecture-concept.webp 1600w"
            sizes="(max-width: 760px) 100vw, 62vw"
            alt="Illustrative contemporary stone and timber home surrounded by a garden at dusk"
            width="1600"
            height="1067"
            fetchPriority="high"
          />
          <div className={styles.annotation} aria-hidden="true">
            Natural materials.
            <br />
            Lasting spaces.
          </div>
          <figcaption>Architectural concept · Not a completed project</figcaption>
        </figure>
      </section>
      <section className={styles.manifesto} aria-label="Our approach">
        <h2>Considered at every scale.</h2>
        <div>
          <span>Architecture</span>
          <small>How a home flows</small>
        </div>
        <div>
          <span>Materials</span>
          <small>How a home feels</small>
        </div>
        <div>
          <span>Craft</span>
          <small>How it comes together</small>
        </div>
      </section>
      <section className={styles.materials} aria-labelledby="materials-title">
        <figure className={styles.materialImage}>
          {/* Swipe / scroll / arrow-key carousel; stays in sync with the buttons. */}
          <div
            ref={trackRef}
            className={styles.materialTrack}
            onScroll={handleTrackScroll}
            // Scrollable regions must be focusable so keyboard users can scroll
            // them with the arrow keys (WCAG 2.1.1; axe scrollable-region-focusable).
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            role="region"
            aria-roledescription="carousel"
            aria-label="Material views — scroll or use arrow keys"
          >
            {MATERIALS.map((item, index) => (
              <div
                key={item.name}
                className={styles.materialSlide}
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${MATERIALS.length}: ${item.name}`}
                aria-hidden={material !== index}
              >
                <img
                  src="/images/materials-concept.webp"
                  alt={`Illustrative detail of natural limestone and oak in warm light — ${item.name.toLowerCase()}`}
                  width="1200"
                  height="800"
                  loading="lazy"
                  draggable={false}
                  style={{ objectPosition: item.position }}
                />
              </div>
            ))}
          </div>
          <figcaption>Illustrative material study</figcaption>
          <span className={styles.materialNumber} aria-hidden="true">
            0{material + 1} / 0{MATERIALS.length}
          </span>
          <div className={styles.materialNav}>
            <button
              type="button"
              aria-label="Previous material"
              onClick={() => goToMaterial(material - 1)}
            >
              <span aria-hidden="true">←</span>
            </button>
            <button
              type="button"
              aria-label="Next material"
              onClick={() => goToMaterial(material + 1)}
            >
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </figure>
        <div className={styles.materialCopy}>
          <p className="eyebrow">A closer look —</p>
          <h2 id="materials-title">
            The details
            <br />
            make a home.
          </h2>
          <div
            className={styles.materialControls}
            role="group"
            aria-label="Explore materials"
          >
            {MATERIALS.map((item, index) => (
              <button
                key={item.name}
                type="button"
                aria-pressed={material === index}
                onClick={() => goToMaterial(index)}
              >
                {item.name}
              </button>
            ))}
          </div>
          <div className={styles.materialDescription} aria-live="polite">
            <h3>{selected.title}</h3>
            <p>{selected.body}</p>
          </div>
          <Link to="/about" className={styles.editorialLink}>
            Get to know Herculean <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>
      <section className={styles.journey} aria-labelledby="journey-title">
        <div>
          <p className="eyebrow">From possibility to place</p>
          <h2 id="journey-title">
            Every home starts
            <br />
            with a conversation.
          </h2>
          <Link to="/gallery" className={styles.editorialLink}>
            Ask about our work <span aria-hidden="true">↗</span>
          </Link>
        </div>
        <ol>
          <li>
            <span>01</span>
            <div>
              <h3>Your way of living</h3>
              <p>Tell us about your plans, your land, and what matters most to you.</p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <h3>The possibilities</h3>
              <p>
                Talk through spaces, materials, priorities, and the questions you want
                answered.
              </p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <h3>The next chapter</h3>
              <p>Understand the next steps for your project before moving forward.</p>
            </div>
          </li>
        </ol>
      </section>
      <section className={styles.invitation}>
        <p className="eyebrow">Make room for what comes next</p>
        <h2>
          What does home
          <br />
          look like to you?
        </h2>
        <Link to="/contact" className="btn btn-primary">
          Let’s talk about it <span aria-hidden="true">↗</span>
        </Link>
      </section>
    </>
  );
}
